import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useUserInvitations() {
  return useQuery({
    queryKey: ["user-invitations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_invitation")
        .select(`
          *,
          invited_by:user_account!invited_by(display_name, email)
        `)
        .in("status", ["pending", "expired"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      // Exclude customer invitations (client_viewer role) — those show on the Customers page
      return (data ?? []).filter((inv: any) => {
        const roles = Array.isArray(inv.roles) ? inv.roles : [];
        return !roles.includes("client_viewer");
      });
    },
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, roles }: { email: string; roles: string[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: userAccount } = await supabase
        .from("user_account")
        .select("id")
        .eq("external_auth_user", user.id)
        .single();

      if (!userAccount) throw new Error("User account not found");

      const { data, error } = await supabase.functions.invoke("send-user-invitation", {
        body: { email, roles, invitedBy: userAccount.id },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-invitations"] });
      toast({
        title: "Invitation sent",
        description: "The user will receive an email with instructions.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error sending invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data: invitation } = await supabase
        .from("user_invitation")
        .select("email, roles, invited_by")
        .eq("id", invitationId)
        .single();

      if (!invitation) throw new Error("Invitation not found");

      // Revoke old invitation
      await supabase
        .from("user_invitation")
        .update({ status: "revoked" })
        .eq("id", invitationId);

      // Create new invitation
      const { data, error } = await supabase.functions.invoke("send-user-invitation", {
        body: {
          email: invitation.email,
          roles: invitation.roles,
          invitedBy: invitation.invited_by,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-invitations"] });
      toast({
        title: "Invitation resent",
        description: "A new invitation email has been sent.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error resending invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("user_invitation")
        .update({ status: "revoked" })
        .eq("id", invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-invitations"] });
      toast({
        title: "Invitation revoked",
        description: "The invitation has been cancelled.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error revoking invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
